import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  taskTitle: string | null;
  saving?: boolean;
  onSave: (text: string) => void;
  onSkip: () => void;
}

/**
 * Jot what happened, right after finishing a task in the timer.
 *
 * Centered prompt rather than a side sheet: it interrupts the same way the
 * resume prompt does, and it is one field. Skipping is a first-class outcome:
 * the note is worth catching while it is fresh, never worth blocking on.
 */
export function CompletionNoteSheet({ open, taskTitle, saving, onSave, onSkip }: Props) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setText('');
      // The point of the prompt is typing into it, so land the caret there.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const save = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      onSkip();
      return;
    }
    onSave(trimmed);
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Skip notes"
        className="absolute inset-0 bg-black/50"
        onClick={onSkip}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add a completion note"
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-surface-1 p-4 shadow-lg"
      >
        <p className="text-body text-text-secondary">
          {taskTitle ? `Finished "${taskTitle}". Anything to note?` : 'Anything to note?'}
        </p>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              save();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onSkip();
            }
          }}
          rows={3}
          placeholder="What happened, what's left, what to remember"
          className={cn(
            'mt-3 w-full resize-y rounded border border-border bg-surface-1 px-2 py-1.5',
            'text-small outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary',
            'focus:border-accent-border',
          )}
        />
        <div className="mt-3 flex flex-col gap-2">
          <Button onClick={save} loading={saving} disabled={text.trim().length === 0}>
            Save note
          </Button>
          <Button variant="secondary" onClick={onSkip} disabled={saving}>
            Skip
          </Button>
        </div>
      </div>
    </div>
  );
}
